import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Tooltip,
  IconButton,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { useSnackBar } from '../../../../contexts/snackbar';
import { CheckCircle, ContentCopy, Launch } from '@mui/icons-material';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import ConfirmAction from '../../../common/confirm-action/confirm-action';
interface WoocommerceIntegrationProps {
  connectedShop: string | null;
  setConnectedShop: (s: string | null) => void;
}
function WoocommerceIntegration({connectedShop, setConnectedShop}:WoocommerceIntegrationProps) {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const [shopUrl, setShopUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [requestedDisconnecting, setRequestedDisconnecing] = useState(false);
  const finialiseInstallationLock = useRef<boolean>(false)

  // No URL validation - accept any input

  // Detect WooCommerce redirect after OAuth
  useEffect(() => {
    const detectRedirectAfterOAuth = async () => {
      // Prevent running if already connected or locked
      if (finialiseInstallationLock.current || connectedShop) return;
      
      const params = new URLSearchParams(window.location.search);
      const event = params.get('event');
      if (event === 'woocommerce_install') {
        const shop = params.get('shop');
        const consumer_key = params.get('consumer_key');
        const consumer_secret = params.get('consumer_secret');

        if (shop && consumer_key && consumer_secret && !connectedShop) {
          finialiseInstallationLock.current = true;
          setIsFinishing(true);
          try {
            await axios.post(`${API_ENDPOINT}/woocommerce/v1/install/`, {
              shop: shop,
              consumer_key: consumer_key,
              consumer_secret: consumer_secret
            }, config);
            setConnectedShop(shop);
            showMessage('WooCommerce integration completed!');
          } catch(e) {
            console.log(e);
            showMessage('WooCommerce integration failed', 'error');
          } finally {
            setIsFinishing(false);
            const newUrl = `${window.location.pathname}?tab=1`;
            window.history.replaceState({}, document.title, newUrl);
          }
        }
      }
    };
  
    detectRedirectAfterOAuth();
  }, [connectedShop, config, showMessage]);
  
  const connectWoocommerce = async () => {
    if (!shopUrl.trim()) {
      showMessage('WooCommerce store URL is required', 'warning');
      return;
    }
    if (!consumerKey.trim()) {
      showMessage('Consumer Key is required', 'warning');
      return;
    }
    if (!consumerSecret.trim()) {
      showMessage('Consumer Secret is required', 'warning');
      return;
    }
    setIsConnecting(true);
    try {
      await axios.post(`${API_ENDPOINT}/woocommerce/v1/install/`, {
        shop: shopUrl, // Use raw input
        consumer_key: consumerKey,
        consumer_secret: consumerSecret
      }, config);
      setConnectedShop(shopUrl);
      showMessage('WooCommerce store connected successfully!');
    } catch (error: unknown) {
      showMessage('Failed to connect WooCommerce store', 'error');
      console.log(error);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWoocommerce = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API_ENDPOINT}/woocommerce/v1/disconnect/`, {}, config);
      setConnectedShop(null);
      showMessage('WooCommerce store disconnected', 'success');
    } catch {
      showMessage('Failed to disconnect WooCommerce store', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const copyShopUrl = () => {
    if (!connectedShop) return;
    navigator.clipboard.writeText(connectedShop);
    showMessage('Shop URL copied to clipboard');
  };

  return (
    <Box sx={{width: "100%", mt: 2}}>
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          {connectedShop ? (
            <>
              <Alert icon={<CheckCircle />} severity="success" sx={{ mb: 2, mt: 2 }}>
                Connected WooCommerce store
              </Alert>
              <TextField
                value={connectedShop}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <>
                      <Tooltip title="Copy shop URL">
                        <IconButton onClick={copyShopUrl}>
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open WooCommerce store">
                        <IconButton
                          onClick={() => window.open(`https://${connectedShop}`, '_blank')}
                        >
                          <Launch />
                        </IconButton>
                      </Tooltip>
                    </>
                  ),
                }}
              />
              <Box display={"flex"} justifyContent={"center"} width={"100%"}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={()=>setRequestedDisconnecing(true)}
                  disabled={isLoading}
                  sx={{ mt: 2 }}
                  size={"small"}
                >
                  Disconnect Store
                </Button>
              </Box>
              
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Connect your WooCommerce store to enable automatic order sync
              </Alert>

              <>
                  <TextField
                    label="WooCommerce Store URL"
                    placeholder="Enter your WooCommerce store URL"
                    value={shopUrl}
                    fullWidth
                    onChange={(e) => setShopUrl(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Woocommerce Consumer Key"
                    placeholder="Enter your WooCommerce Consumer Key"
                    value={consumerKey}
                    name="Consumer Key"
                    autoComplete='off'
                    fullWidth
                    onChange={(e) => setConsumerKey(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    label="Woocommerce Consumer Secret"
                    placeholder="Enter your WooCommerce Consumer Secret"
                    value={consumerSecret}
                    fullWidth
                    name="Woocommerce Consumer Secret"
                    onChange={(e) => setConsumerSecret(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                </>

              <Button
                variant="contained"
                onClick={() => {
                    connectWoocommerce()
                }}
                disabled={(isConnecting || !shopUrl || !consumerKey || !consumerSecret)}
              >
                Connect to WooCommerce {isConnecting && <CircularProgress size={20} />}
              </Button>
            </>
          )}
        </>
      )}

      <CustomDialog open={isFinishing}>
        <DialogTitle>Finishing Woocommerce installation</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Please wait while we complete the setup...</Typography>
        </DialogContent>
      </CustomDialog>

      {
        requestedDisconnecting && <ConfirmAction 
          message={"Are you sure you want to disconnect your woocommerce store?"} 
          action={disconnectWoocommerce} 
          isOpen={requestedDisconnecting} 
          handleClose={()=>setRequestedDisconnecing(false)}
          isSevereAction
          confirmSevereActionTarget={"Confirm"}
          >
          <Alert severity='warning'>
            Disconnecting your woocommerce store will not delete orders previously imported from woocommerce.
          </Alert>
        </ConfirmAction>
      }
    </Box>
  );
}

export default WoocommerceIntegration;
